/**
 * Query and mutation hooks for Support.
 *
 * Keys follow DESIGN.md section 6: ['support', queue, filtersHash, cursor] and
 * ['ticket', ref]. Live class data refetches every 30 seconds; the handoff transcript polls
 * every 4 seconds while it is open, matching the customer widget.
 *
 * Assign, tag and internal note are optimistic. Reply, bulk and every remedy request are
 * not, because they leave the building.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  BulkPreview,
  HandoffMessage,
  HandoffRow,
  Page,
  QueueCounts,
  SupportMacroRow,
  SupportQueue,
  TicketListRow,
  TicketWorkspaceData,
} from './types';
import type { SupportBulkJob } from '@elutide/shared';

export interface TicketFilters {
  status?: string[];
  priority?: string[];
  sla?: string;
  q?: string;
  mine?: boolean;
}

export function filtersHash(filters: TicketFilters): string {
  return JSON.stringify({
    status: [...(filters.status ?? [])].sort(),
    priority: [...(filters.priority ?? [])].sort(),
    sla: filters.sla ?? '',
    q: filters.q ?? '',
    mine: filters.mine ?? false,
  });
}

export function useTickets(
  queue: SupportQueue,
  filters: TicketFilters,
  cursor: string | null,
): UseQueryResult<Page<TicketListRow>> {
  return useQuery({
    queryKey: ['support', queue, filtersHash(filters), cursor],
    queryFn: () =>
      api.get<Page<TicketListRow>>('/support/tickets', {
        query: {
          queue,
          ...(filters.status?.length ? { status: filters.status } : {}),
          ...(filters.priority?.length ? { priority: filters.priority } : {}),
          ...(filters.sla ? { sla: filters.sla } : {}),
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.mine ? { mine: true } : {}),
          ...(cursor ? { cursor } : {}),
        },
      }),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

export function useQueueCounts(): UseQueryResult<QueueCounts> {
  return useQuery({
    queryKey: ['support', 'queues'],
    queryFn: () => api.get<QueueCounts>('/support/queues'),
    refetchInterval: 30_000,
  });
}

export function useTicket(ref: string): UseQueryResult<TicketWorkspaceData> {
  return useQuery({
    queryKey: ['ticket', ref],
    queryFn: () => api.get<TicketWorkspaceData>(`/support/tickets/${encodeURIComponent(ref)}`),
    refetchInterval: 30_000,
  });
}

export function useMacros(): UseQueryResult<Page<SupportMacroRow>> {
  return useQuery({
    queryKey: ['support', 'macros'],
    queryFn: () => api.get<Page<SupportMacroRow>>('/support/macros'),
    staleTime: 5 * 60_000,
  });
}

export interface ReplyInput {
  body: string;
  channel: 'email' | 'sms';
  macro_id: string | null;
  set_status: string | null;
  hold_seconds: number;
  reason: string | null;
}

export function useSendReply(ref: string): UseMutationResult<unknown, unknown, ReplyInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.post(`/support/tickets/${encodeURIComponent(ref)}/reply`, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['ticket', ref] });
      void client.invalidateQueries({ queryKey: ['support'] });
    },
  });
}

export function useAddInternalNote(ref: string): UseMutationResult<unknown, unknown, { body: string }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.post(`/support/tickets/${encodeURIComponent(ref)}/notes`, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['ticket', ref] });
    },
  });
}

export function useSaveDraft(ref: string): UseMutationResult<unknown, unknown, string> {
  return useMutation({
    mutationFn: (body) => api.put(`/support/tickets/${encodeURIComponent(ref)}/draft`, { body }),
  });
}

export function useSetStatus(ref: string): UseMutationResult<unknown, unknown, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (status) => api.put(`/support/tickets/${encodeURIComponent(ref)}/status`, { status }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['ticket', ref] });
      void client.invalidateQueries({ queryKey: ['support'] });
    },
  });
}

export function useBulkPreview(): UseMutationResult<
  BulkPreview,
  unknown,
  { action: string; ticket_ids: number[]; params: Record<string, unknown>; notify: boolean }
> {
  return useMutation({
    mutationFn: (input) => api.post<BulkPreview>('/support/bulk/preview', input),
  });
}

export function useBulkApply(): UseMutationResult<
  SupportBulkJob,
  unknown,
  { action: string; ticket_ids: number[]; params: Record<string, unknown>; notify: boolean; reason: string | null }
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.post<SupportBulkJob>('/support/bulk', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['support'] });
    },
  });
}

export function useBulkUndo(): UseMutationResult<SupportBulkJob, unknown, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (jobId) => api.post<SupportBulkJob>(`/support/bulk/${jobId}/undo`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['support'] });
    },
  });
}

export function useHandoffs(): UseQueryResult<Page<HandoffRow>> {
  return useQuery({
    queryKey: ['support', 'handoffs'],
    queryFn: () => api.get<Page<HandoffRow>>('/support/handoffs'),
    refetchInterval: 10_000,
  });
}

export function useHandoff(
  handoffId: number,
): UseQueryResult<{ handoff: HandoffRow; messages: HandoffMessage[] }> {
  return useQuery({
    queryKey: ['support', 'handoff', handoffId],
    queryFn: () => api.get<{ handoff: HandoffRow; messages: HandoffMessage[] }>(`/support/handoffs/${handoffId}`),
    // Four seconds, matching the customer widget poll, so both seats see the same thing.
    refetchInterval: 4_000,
  });
}

export function useHandoffAction(
  handoffId: number,
): UseMutationResult<unknown, unknown, { action: 'reply' | 'instruct' | 'close' | 'convert'; body?: string; reason?: string }> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ action, ...rest }) => api.post(`/support/handoffs/${handoffId}/${action}`, rest),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['support', 'handoff', handoffId] });
    },
  });
}

export function useRemedyRequest(
  ref: string,
): UseMutationResult<
  unknown,
  unknown,
  { kind: 'rr_credit' | 'refund' | 'comp' | 'reship'; amount_cents: number; reason: string; order_id: number | null }
> {
  return useMutation({
    mutationFn: (input) => api.post(`/support/tickets/${encodeURIComponent(ref)}/requests`, input),
  });
}
